/**
 * Page Loader Module
 * File: page-loader.js
 * Location: Theme/src/js/custom/common/page-loader.js
 *
 * Drives the centre-page RBF logo animation rendered by
 * Views/Shared/Components/Partials/_PageLoader.cshtml.
 *
 * It shows itself for same-site link navigation. Anything else that keeps the visitor
 * waiting (application submit, status search, info response) calls
 * RbfPageLoader.show(caption) / RbfPageLoader.hide() around its own work.
 *
 * Security Compliance: VAPT M25 1806 Reserve Bank of Fiji
 * - External script file, no inline handlers (CSP Compliant - CRITICAL-003)
 * - Captions go in through textContent, never innerHTML (XSS Prevention - MEDIUM-004)
 *
 * JavaScript Class Declaration Style:
 * - Function-type classes as per C# .NET Core Clean Architecture Guide
 * - Expose public methods through return statement
 * - Use const and let (no var)
 */

"use strict";

/**
 * Page Loader Module
 * @returns {Object} Public API methods
 */
const RbfPageLoader = (function () {
    // ============================================
    // PRIVATE CONSTANTS
    // ============================================

    const SELECTORS = Object.freeze({
        root: '[data-page-loader]',
        caption: '[data-pl-caption]',
        ignore: '[data-page-loader-ignore]'
    });

    const VISIBLE_CLASS = 'is-visible';
    const DEFAULT_CAPTION = 'Loading';

    // Once up, the loader stays for at least this long. Work that finishes in 80ms would
    // otherwise flash it for a frame or two, which reads as a glitch rather than as polish.
    const MIN_VISIBLE_MS = 1200;

    // A link click is held this long before the browser is sent on its way, so the
    // animation is seen even when the next page is ready instantly.
    const NAVIGATION_HOLD_MS = 900;

    // Links to files answer with a download or a viewer, not a new page, so the current
    // page never unloads and nothing would ever take the loader down again.
    const FILE_LINK_PATTERN = /\.(pdf|docx?|xlsx?|csv|zip|png|jpe?g|gif|svg)$/i;

    // ============================================
    // PRIVATE STATE
    // ============================================

    let root = null;
    let caption = null;
    let hideTimer = null;
    let shownAt = 0;

    // ============================================
    // PRIVATE METHODS
    // ============================================

    const clearHideTimer = function () {
        if (hideTimer !== null) {
            window.clearTimeout(hideTimer);
            hideTimer = null;
        }
    };

    const conceal = function () {
        clearHideTimer();
        root.classList.remove(VISIBLE_CLASS);
        root.setAttribute('aria-hidden', 'true');
    };

    /**
     * Whether following this link replaces the current page with another page of the portal.
     * @param {HTMLAnchorElement} link
     * @returns {boolean}
     */
    const isPageNavigation = function (link) {
        if (link.hasAttribute('download') || link.closest(SELECTORS.ignore)) {
            return false;
        }

        const target = link.getAttribute('target');
        if (target && target.toLowerCase() !== '_self') {
            return false;
        }

        // link.href is the resolved URL, so this also drops mailto:, tel: and javascript:
        let url;
        try {
            url = new URL(link.href);
        } catch (e) {
            return false;
        }

        if (url.origin !== window.location.origin || FILE_LINK_PATTERN.test(url.pathname)) {
            return false;
        }

        // Same page with a hash is an in-page jump, not a navigation
        const samePage = url.pathname === window.location.pathname && url.search === window.location.search;
        return !(samePage && url.hash);
    };

    const handleLinkClick = function (evt) {
        // Modified clicks open a new tab or window; this page stays where it is
        if (evt.defaultPrevented || evt.button !== 0 || evt.ctrlKey || evt.metaKey || evt.shiftKey || evt.altKey) {
            return;
        }

        const link = evt.target.closest ? evt.target.closest('a[href]') : null;
        if (!link || !isPageNavigation(link)) {
            return;
        }

        show();

        // Nothing is animating for these visitors, so holding them back buys nothing
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        evt.preventDefault();
        const destination = link.href;
        window.setTimeout(function () {
            window.location.assign(destination);
        }, NAVIGATION_HOLD_MS);
    };

    // ============================================
    // PUBLIC METHODS
    // ============================================

    /**
     * Show the loader straight away.
     * @param {string} [text] Caption shown under the logo, without trailing dots
     */
    const show = function (text) {
        if (!root) {
            return;
        }

        if (caption) {
            caption.textContent = text || DEFAULT_CAPTION;
        }

        // A hide() still waiting out the minimum belongs to the previous piece of work
        clearHideTimer();

        if (!root.classList.contains(VISIBLE_CLASS)) {
            shownAt = Date.now();
            root.classList.add(VISIBLE_CLASS);
            root.setAttribute('aria-hidden', 'false');
        }
    };

    /**
     * Take the loader down, once it has been up for the minimum visible time.
     */
    const hide = function () {
        if (!root || !root.classList.contains(VISIBLE_CLASS) || hideTimer !== null) {
            return;
        }

        const remaining = MIN_VISIBLE_MS - (Date.now() - shownAt);
        if (remaining > 0) {
            hideTimer = window.setTimeout(conceal, remaining);
        } else {
            conceal();
        }
    };

    const init = function () {
        root = document.querySelector(SELECTORS.root);
        if (!root) {
            return;
        }

        caption = root.querySelector(SELECTORS.caption);

        // Bubble phase on document, so a handler that calls preventDefault() on its own
        // link has already run and defaultPrevented is reliable.
        document.addEventListener('click', handleLinkClick);

        // Browsers restore a page from the bfcache with the DOM exactly as it was left,
        // which would bring the visitor back to a loader that is waiting for nothing.
        // conceal, not hide: there is no minimum to honour on a page nobody is waiting for.
        window.addEventListener('pageshow', conceal);
    };

    return {
        init: init,
        show: show,
        hide: hide
    };
})();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', RbfPageLoader.init);
} else {
    RbfPageLoader.init();
}
